/**
 * Shared error-shape handling for axios responses.
 *
 * FastAPI uses two different shapes for the `detail` field:
 *
 *   - Routine 4xx (401, 403, 404, 409, etc.) → `{ detail: "<string>" }`.
 *     The string is meant for end users; we just show it.
 *
 *   - Pydantic validation (422) → `{ detail: [{ type, loc, msg, ... }, ...] }`.
 *     An array of error objects. Each entry pinpoints a single
 *     request field; the array is what FastAPI builds from
 *     `RequestValidationError.errors()`.
 *
 * Forgetting the second shape used to be a real bug: setting React
 * state to an array of objects and then rendering it inside `<Alert>`
 * threw "Objects are not valid as a React child" and unmounted the
 * page. AcceptInvite hit it whenever a too-short password landed a
 * 422 — see 2026-06-05 incident.
 *
 * This helper coerces either shape into a renderable string, with a
 * generic fallback when neither matches. `fallback` lets the caller
 * tailor the message ("registration failed", "could not save…").
 */
export function extractErrorMessage(
  err: unknown,
  fallback = 'request failed',
): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const item = d as { loc?: unknown[]; msg?: string };
        // Last loc segment is the field name; earlier segments are
        // structural ("body", "query", ...) that the end user
        // doesn't need to see.
        const field =
          Array.isArray(item.loc) && item.loc.length > 0
            ? String(item.loc[item.loc.length - 1])
            : 'field';
        return `${field}: ${item.msg ?? 'invalid'}`;
      })
      .join('; ');
  }
  // Plain Error (e.g. fetch() rejection on CORS, an explicit throw
  // from a helper): use its message so non-axios paths surface
  // real diagnostics instead of falling through to the bland
  // fallback string.
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
